import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "block text-sm font-medium text-gray-700 mb-1"
}
const _hoisted_2 = {
  key: 0,
  class: "text-red-500"
}
const _hoisted_3 = { class: "relative" }
const _hoisted_4 = ["id", "value", "placeholder", "disabled", "min", "max"]
const _hoisted_5 = {
  key: 1,
  class: "mt-1 text-sm text-red-600"
}

import { ref, computed, watch } from 'vue'
import type { DateFieldProps } from '@/types/sellerRegister'


export default /*@__PURE__*/_defineComponent({
  __name: 'DateField',
  props: {
    label: {},
    required: { type: Boolean, default: false },
    placeholder: {},
    modelValue: {},
    disabled: { type: Boolean, default: false },
    gridClass: { default: '' },
    helpText: {},
    customClass: {},
    minDate: {},
    maxDate: {},
    forbiddenDates: {},
    forbiddenWeekDays: {},
    showValidationInfo: { type: Boolean, default: false }
  },
  emits: ["update:modelValue", "validation-changed"],
  setup(__props: any, { emit: __emit }) {

const props = __props

const emit = __emit

// État local
const errorMessage = ref('')
const hasBeenTouched = ref(false)

// ID unique
const uniqueId = computed(() => `date-${Math.random().toString(36).substr(2, 9)}`)

// Dates min/max calculées
const minDate = computed(() => {
  return props.minDate || undefined
})

const maxDate = computed(() => {
  if (props.maxDate) return props.maxDate
  
  // Par défaut, pas de date maximum (peut être dans le futur)
  return undefined
})

// Validation de la date
const validateDate = () => {
  if (!props.modelValue) {
    if (props.required) {
      emit('validation-changed', false)
      return false
    }
    errorMessage.value = ''
    emit('validation-changed', true)
    return true
  }

  const selectedDate = new Date(props.modelValue)
  
  // Vérifier que c'est une date valide
  if (isNaN(selectedDate.getTime())) {
    errorMessage.value = 'Date invalide'
    emit('validation-changed', false)
    return false
  }
  
  // Vérifier la date minimum
  if (minDate.value) {
    const minDateObj = new Date(minDate.value)
    if (selectedDate < minDateObj) {
      errorMessage.value = `La date doit être après le ${formatDateForDisplay(minDate.value)}`
      emit('validation-changed', false)
      return false
    }
  }
  
  // Vérifier la date maximum
  if (maxDate.value) {
    const maxDateObj = new Date(maxDate.value)
    if (selectedDate > maxDateObj) {
      errorMessage.value = `La date doit être avant ou égale au ${formatDateForDisplay(maxDate.value)}`
      emit('validation-changed', false)
      return false
    }
  }
  
  // Vérifier les dates interdites
  if (props.forbiddenDates && props.forbiddenDates.includes(props.modelValue)) {
    errorMessage.value = 'Cette date n\'est pas autorisée'
    emit('validation-changed', false)
    return false
  }
  
  // Vérifier les jours de la semaine interdits
  if (props.forbiddenWeekDays && props.forbiddenWeekDays.includes(selectedDate.getDay())) {
    const weekDays = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi']
    errorMessage.value = `Les ${weekDays[selectedDate.getDay()]}s ne sont pas autorisés`
    emit('validation-changed', false)
    return false
  }
  
  errorMessage.value = ''
  emit('validation-changed', true)
  return true
}

// Gestion de l'input
const handleDateInput = (event: Event) => {
  const value = (event.target as HTMLInputElement).value
  emit('update:modelValue', value)
  
  if (!hasBeenTouched.value) {
    hasBeenTouched.value = true
  }
}

// Formatage de la date pour l'affichage
const formatDateForDisplay = (dateString: string): string => {
  const date = new Date(dateString)
  return date.toLocaleDateString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  })
}

// Watcher pour valider automatiquement
watch(() => props.modelValue, () => {
  if (hasBeenTouched.value) {
    validateDate()
  }
})

// Validation initiale si une valeur est déjà présente
watch(() => props.modelValue, (newValue) => {
  if (newValue && !hasBeenTouched.value) {
    validateDate()
  }
}, { immediate: true })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(_ctx.gridClass)
  }, [
    (_ctx.label)
      ? (_openBlock(), _createElementBlock("label", _hoisted_1, [
          _createTextVNode(_toDisplayString(_ctx.label) + " ", 1),
          (_ctx.required)
            ? (_openBlock(), _createElementBlock("span", _hoisted_2, "*"))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_3, [
      _createElementVNode("input", {
        id: uniqueId.value,
        type: "date",
        value: _ctx.modelValue,
        placeholder: _ctx.placeholder,
        disabled: _ctx.disabled,
        min: minDate.value,
        max: maxDate.value,
        onInput: handleDateInput,
        onBlur: validateDate,
        class: _normalizeClass([
          'w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors',
          _ctx.disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white',
          errorMessage.value ? 'border-red-300 focus:ring-red-500 focus:border-red-500' : 'border-gray-300',
          _ctx.customClass
        ])
      }, null, 42, _hoisted_4)
    ]),
    (errorMessage.value && hasBeenTouched.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(errorMessage.value), 1))
      : _createCommentVNode("", true)
  ], 2))
}
}

})